import React, { useState, useEffect } from 'react';
import { adminService } from '../../services';
import PageHeader from '../../components/common/PageHeader';
import { USER_ROLES } from '../../constants/config';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/formatters';

// Admin roles (excluding USER)
const ADMIN_ROLES = [
  USER_ROLES.ADMIN,
  USER_ROLES.FINANCE_ADMIN,
  USER_ROLES.CONTENT_ADMIN,
  USER_ROLES.SUPPORT_ADMIN,
];

const getRoleDisplayName = (role, t) => {
  switch (role) {
    case USER_ROLES.ADMIN:
      return t('admin.admins.roles.admin');
    case USER_ROLES.FINANCE_ADMIN:
      return t('admin.admins.roles.financeAdmin');
    case USER_ROLES.CONTENT_ADMIN:
      return t('admin.admins.roles.contentAdmin');
    case USER_ROLES.SUPPORT_ADMIN:
      return t('admin.admins.roles.supportAdmin');
    default:
      return role;
  }
};

const AdminManagement = () => {
  const { t, i18n } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');

  // Staff registration form
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    role: USER_ROLES.ADMIN,
  });
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, roleFilter]);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError('');

      // Backend endpoint no longer supports role filtering via query.
      // Backend caps limit at 200, so fetch all pages and filter client-side by role.
      const pageLimit = 200;
      let offset = 0;
      let allUsers = [];
      let safety = 0;

      while (safety < 20) {
        // up to 4000 users max in one fetch to avoid infinite loops
        safety += 1;
        // eslint-disable-next-line no-await-in-loop
        const response = await adminService.getAllUsers({
          limit: pageLimit,
          offset,
          sortBy: 'createdAt',
          order: 'DESC',
        });

        const items = response?.data?.items || response?.items || [];
        allUsers = allUsers.concat(items);

        if (items.length < pageLimit) break;
        offset += pageLimit;
      }

      const adminRoles = ADMIN_ROLES;

      let allAdmins = allUsers.filter((u) => adminRoles.includes(u.role));
      if (roleFilter !== 'ALL') {
        allAdmins = allAdmins.filter((u) => u.role === roleFilter);
      }

      allAdmins.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

      const total = allAdmins.length;
      const startIndex = (currentPage - 1) * limit;
      const paginatedAdmins = allAdmins.slice(startIndex, startIndex + limit);

      setAdmins(paginatedAdmins);
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching admins:', err);
      setError(t('admin.admins.messages.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setFormData({
      fullName: '',
      email: '',
      password: '',
      role: USER_ROLES.ADMIN,
    });
    setShowModal(true);
    setError('');
    setSuccess('');
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');
    setSuccess('');

    // Validation
    if (!formData.fullName.trim()) {
      setError(t('admin.admins.messages.validationName'));
      setCreating(false);
      return;
    }

    if (!formData.email.trim()) {
      setError(t('admin.admins.messages.validationEmail'));
      setCreating(false);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('admin.admins.messages.validationEmailInvalid'));
      setCreating(false);
      return;
    }

    if (!formData.password || formData.password.length < 8) {
      setError(t('admin.admins.messages.validationPassword'));
      setCreating(false);
      return;
    }

    try {
      const payload = {
        fullName: formData.fullName.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: formData.role,
      };

      await adminService.registerStaff(payload);

      setSuccess(t('admin.admins.messages.createSuccess', { role: getRoleDisplayName(formData.role, t) }));
      setShowModal(false);
      setFormData({
        fullName: '',
        email: '',
        password: '',
        role: USER_ROLES.ADMIN,
      });

      // Refresh the list
      fetchAdmins();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error creating admin:', err);
      const errorMessage = err.response?.data?.message || t('admin.admins.messages.createError');
      setError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
    setFormData({
      fullName: '',
      email: '',
      password: '',
      role: USER_ROLES.ADMIN,
    });
  };

  const totalPages = Math.ceil(totalCount / limit);

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case USER_ROLES.ADMIN:
        return 'bg-purple-100 text-purple-800';
      case USER_ROLES.FINANCE_ADMIN:
        return 'bg-green-100 text-green-800';
      case USER_ROLES.CONTENT_ADMIN:
        return 'bg-blue-100 text-blue-800';
      case USER_ROLES.SUPPORT_ADMIN:
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading && admins.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 pb-8">
      <PageHeader
        title={t('admin.admins.title')}
        subtitle={t('admin.admins.subtitle')}
        icon={
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        }
      />
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
            >
              <option value="ALL">{t('admin.admins.roles.all')}</option>
              {ADMIN_ROLES.map(role => (
                <option key={role} value={role}>{getRoleDisplayName(role, t)}</option>
              ))}
            </select>
            <button
              onClick={handleCreateNew}
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              {t('admin.admins.buttons.addAdmin')}
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && !showModal && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {/* Admin List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-100 border-b-2 border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.admins.table.name')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.admins.table.email')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.admins.table.role')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.admins.table.status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('admin.admins.table.createdAt')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      {t('admin.admins.table.noAdmins')}
                    </td>
                  </tr>
                ) : (
                  admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-primary-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-primary-600 font-medium text-sm">
                                {admin.fullName?.charAt(0)?.toUpperCase() || 'A'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {admin.fullName || 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{admin.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getRoleBadgeColor(admin.role)}`}>
                          {getRoleDisplayName(admin.role, t)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${admin.isActive !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {admin.isActive !== false ? t('admin.users.status.active') : t('admin.users.status.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(admin.createdAt, i18n.language)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    {t('common.pagination.showing')} <span className="font-medium">{(currentPage - 1) * limit + 1}</span> {t('common.pagination.to')}{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, totalCount)}</span> {t('common.pagination.of')}{' '}
                    <span className="font-medium">{totalCount}</span> {t('common.pagination.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {t('actions.previous')}
                    </button>
                    {(() => {
                      const maxButtons = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
                      let endPage = Math.min(totalPages, startPage + maxButtons - 1);

                      if (endPage - startPage + 1 < maxButtons) {
                        startPage = Math.max(1, endPage - maxButtons + 1);
                      }

                      const pages = [];
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }

                      return pages.map((pageNum) => (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === pageNum
                            ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      ));
                    })()}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-primary-50 disabled:opacity-50"
                    >
                      {t('actions.next')}
                    </button>
                  </nav>
                </div>
              </div>
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50"
                >
                  {t('actions.previous')}
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t('actions.next')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create Admin Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">{t('admin.admins.form.createTitle')}</h2>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {error && showModal && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.admins.form.fullName')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('admin.admins.form.fullNamePlaceholder')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.admins.form.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder={t('admin.admins.form.emailPlaceholder')}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.admins.form.password')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent pr-10"
                        placeholder={t('admin.admins.form.passwordPlaceholder')}
                        required
                        minLength={8}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                          </svg>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {t('admin.admins.form.passwordHint')}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('admin.admins.form.role')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
                      required
                    >
                      {ADMIN_ROLES.map(role => (
                        <option key={role} value={role}>{getRoleDisplayName(role, t)}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.role === USER_ROLES.ADMIN && t('admin.admins.form.adminDesc')}
                      {formData.role === USER_ROLES.FINANCE_ADMIN && t('admin.admins.form.financeDesc')}
                      {formData.role === USER_ROLES.CONTENT_ADMIN && t('admin.admins.form.contentDesc')}
                      {formData.role === USER_ROLES.SUPPORT_ADMIN && t('admin.admins.form.supportDesc')}
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                    >
                      {t('admin.admins.buttons.cancel')}
                    </button>
                    <button
                      type="submit"
                      disabled={creating}
                      className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {creating ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {t('common.creating')}
                        </>
                      ) : (
                        t('admin.admins.buttons.createAdmin')
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;
